import { usePostCandidatesFetchCommunicationMethods } from "@api";
import type { IModalActionForm, IModalActionResolver } from "@complex/Actions/Modal/logics/types";
import { TZDate } from "@date-fns/tz";
import { useGlobalContext } from "@root/context";
import {
  getCandidateAvailableCommunications,
  getCandidateCommunicationPreferences,
  getCandidateMissingContactDetails,
} from "@root/utils/candidate/communicationPreferences";
import { isAppointmentReminderSchedulable } from "@root/utils/date/appointmentReminder";
import { CommunicationType } from "@root/utils/enums/communication";
import { useI18nContext } from "@root/utils/organisation";
import { addMinutes, startOfDay } from "date-fns";
import { useLayoutEffect } from "react";
import { type Control, useWatch } from "react-hook-form";

export enum EnumActionDateStatus {
  UNSET = "UNSET",
  TOO_SOON = "TOO_SOON",
  SCHEDULABLE = "SCHEDULABLE",
}

export enum EnumAppointmentReminderMissingCommunicationPreferenceReason {
  COMMUNICATION_MISSING = "COMMUNICATION_MISSING",
  CANDIDATE_MISSING = "CANDIDATE_MISSING",
}

export const getTooltipTitle = (
  candidateCommunicationPreference: CommunicationType | null,
  appointmentReminderCommunication: CommunicationType | null,
  actionDateStatus: EnumActionDateStatus,
  missingContactDetails: string[]
) => {
  // Only a candidate reachable by no channel at all blocks the reminder: one usable channel is
  // enough, so a candidate with a phone and an SMS opt-in but no mail keeps the option. Missing
  // contact details then only refine the reason shown.
  if (candidateCommunicationPreference === null) {
    if (missingContactDetails.length > 0) {
      return `L'option d'envoyer une communication de rappel aux candidats avant le rendez-vous ne peut pas être activé car ${missingContactDetails.join(" et ")} ${missingContactDetails.length > 1 ? "ne sont pas renseignés" : "n'est pas renseigné"} sur la fiche candidat.`;
    }
    return "L'option d'envoyer une communication de rappel aux candidats avant le rendez-vous ne peut pas être activé car le candidat n’a accepté aucun moyen de communication (Email, SMS).";
  }
  if (appointmentReminderCommunication === null) {
    return "L'option d'envoyer une communication de rappel aux candidats avant le rendez-vous ne peut pas être activé car cette option est désactivée dans les paramètres de votre organisation.";
  }
  if (actionDateStatus === EnumActionDateStatus.UNSET) {
    return "L'option d'envoyer une communication de rappel aux candidats avant le rendez-vous ne peut pas être activé car aucune date n'est renseignée dans l'action.";
  }
  if (actionDateStatus === EnumActionDateStatus.TOO_SOON) {
    return "L'option d'envoyer une communication de rappel aux candidats avant le rendez-vous ne peut pas être activé car ce rappel, envoyé un jour ouvré 24h avant le rendez-vous, aurait dû partir avant aujourd'hui.";
  }
};

/** The action start, i.e. the selected day at the selected start time (midnight when unset). */
export const getActionStartDateTime = (date: Date | null, startTime: number | "", timeZone: string) =>
  date === null ? null : addMinutes(startOfDay(new TZDate(date, timeZone)), startTime || 0);

export const computeActionDateStatus = (startDateTime: Date | null, timeZone: string) => {
  if (startDateTime === null) {
    return EnumActionDateStatus.UNSET;
  }
  return isAppointmentReminderSchedulable(startDateTime, timeZone)
    ? EnumActionDateStatus.SCHEDULABLE
    : EnumActionDateStatus.TOO_SOON;
};

export const useAppointmentReminderTooltipTitle = (
  control: Control<IModalActionForm, void, IModalActionResolver>,
  candidateCommunicationPreference: CommunicationType | null,
  appointmentReminderCommunication: CommunicationType | null,
  missingContactDetails: string[]
) => {
  const { timeZone } = useI18nContext();
  const actionDateStatus = useWatch({
    control,
    name: ["date", "startTime"],
    compute: ([date, startTime]) =>
      computeActionDateStatus(getActionStartDateTime(date, startTime, timeZone), timeZone),
  });
  const tooltipTitle = getTooltipTitle(
    candidateCommunicationPreference,
    appointmentReminderCommunication,
    actionDateStatus,
    missingContactDetails
  );
  return tooltipTitle;
};

export const useAppointmentReminderCandidateCommunicationPreferences = (
  control: Control<IModalActionForm, void, IModalActionResolver>
) => {
  const candidateId = useWatch({
    control,
    name: "candidate",
    compute: (candidate) => candidate?._id,
  });
  const [{ data, loading }, fetchCandidatesCommunicationMethods] = usePostCandidatesFetchCommunicationMethods();
  const communicationPreference = getCandidateCommunicationPreferences(data?.[0]);
  const availableCommunications = getCandidateAvailableCommunications(data?.[0]);
  const missingContactDetails = candidateId ? getCandidateMissingContactDetails(data?.[0]) : [];
  // Until the candidate methods are fetched, every channel looks unavailable: staying on "loading"
  // avoids briefly disabling a switch that the candidate is in fact allowed to receive.
  const resolving = loading || (Boolean(candidateId) && data === undefined);

  let communicationMissingReason: EnumAppointmentReminderMissingCommunicationPreferenceReason | null = null;
  if (communicationPreference === null) {
    communicationMissingReason = candidateId
      ? EnumAppointmentReminderMissingCommunicationPreferenceReason.COMMUNICATION_MISSING
      : EnumAppointmentReminderMissingCommunicationPreferenceReason.CANDIDATE_MISSING;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetch candidates communication methods only on candidate change
  useLayoutEffect(() => {
    if (candidateId) {
      fetchCandidatesCommunicationMethods([candidateId]);
    }
  }, [candidateId]);

  return [
    communicationPreference,
    availableCommunications,
    resolving,
    communicationMissingReason,
    missingContactDetails,
  ] as const;
};

export const useAppointmentReminderCommunication = (
  communicationPreference: CommunicationType | null,
  availableCommunications: Set<CommunicationType>
) => {
  const { user } = useGlobalContext();

  if (communicationPreference === null) {
    return null;
  }
  switch (communicationPreference) {
    case CommunicationType.SMS:
      if (user.organisation.features.appointmentReminder?.action?.sms) {
        return CommunicationType.SMS;
      }
      break;
    case CommunicationType.EMAIL:
      if (user.organisation.features.appointmentReminder?.action?.email) {
        return CommunicationType.EMAIL;
      }
      break;
  }

  if (
    availableCommunications.has(CommunicationType.SMS) &&
    user.organisation.features.appointmentReminder?.action?.sms
  ) {
    return CommunicationType.SMS;
  }
  if (
    availableCommunications.has(CommunicationType.EMAIL) &&
    user.organisation.features.appointmentReminder?.action?.email
  ) {
    return CommunicationType.EMAIL;
  }
  return null;
};

export const getAppointmentReminderSwitchLabel = (communicationPreference: CommunicationType | null) => {
  switch (communicationPreference) {
    case CommunicationType.SMS:
      return "Envoyer un SMS de rappel au candidat 1 jour avant le rendez-vous";
    case CommunicationType.EMAIL:
      return "Envoyer un Email de rappel au candidat 1 jour avant le rendez-vous";
    default:
      return "Envoyer un (SMS ou Email) de rappel au candidat 1 jour avant le rendez-vous";
  }
};
